import type { ReactNode } from "react";
import {
  ArrowUpRight,
  Facebook,
  Ghost,
  Instagram,
  Music2,
  Phone,
  Youtube,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { useI18n } from "@/lib/i18n";
import { loc } from "@/lib/format";
import { useStorefront, useStorefrontConfig } from "@/lib/storefront";
import { hasLegalText } from "@/lib/legal";

const footerLinkClass =
  "group inline-flex min-h-10 items-center gap-1.5 py-1 text-sm font-medium text-primary-foreground/75 transition-colors hover:text-primary-foreground";

export function Footer({ flush = false }: { flush?: boolean }) {
  const { t, lang } = useI18n();
  const bootstrap = useStorefront();
  const config = useStorefrontConfig();
  const year = new Date().getFullYear();
  const brand = loc(config.title, lang);
  const supportPhone = bootstrap.tenant.supportPhone.trim();
  const socialLinks = [
    { key: "facebook", label: "Facebook", icon: Facebook },
    { key: "instagram", label: "Instagram", icon: Instagram },
    { key: "snapchat", label: "Snapchat", icon: Ghost },
    { key: "youtube", label: "YouTube", icon: Youtube },
    { key: "tiktok", label: "TikTok", icon: Music2 },
  ] as const;
  const availableSocialLinks = socialLinks.flatMap(
    ({ key, label, icon: Icon }) => {
      const href = bootstrap.tenant.socialLinks?.[key];
      return href ? [{ key, label, href, Icon }] : [];
    },
  );
  const legalLinks = [
    hasLegalText(bootstrap, "privacy")
      ? { to: "/privacy-policy" as const, label: t("footer.privacy") }
      : null,
    hasLegalText(bootstrap, "terms")
      ? { to: "/terms-and-conditions" as const, label: t("footer.terms") }
      : null,
    hasLegalText(bootstrap, "refund")
      ? { to: "/return-policy" as const, label: t("footer.returns") }
      : null,
  ].filter((link): link is NonNullable<typeof link> => link !== null);

  return (
    <footer className={`${flush ? "mt-0" : "mt-10"} border-t border-gold/35 bg-aubergine text-primary-foreground`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 py-6 sm:gap-x-8 sm:py-8 lg:grid-cols-12 lg:gap-x-10 lg:gap-y-6">
          <div className="col-span-2 lg:col-span-5 lg:pe-10">
            <Link
              to="/"
              aria-label={brand}
              className="inline-flex min-h-11 max-w-full items-center gap-3 rounded-lg outline-offset-4"
            >
              {config.logo ? (
                <span className="grid h-14 min-w-14 place-items-center overflow-hidden rounded-xl border border-white/15 bg-white/95 px-2 shadow-sm">
                  <ResponsiveImage
                    source={config.logo}
                    sizes="112px"
                    alt=""
                    loading="lazy"
                    className="max-h-10 w-auto max-w-28 object-contain"
                    width={112}
                    height={40}
                  />
                </span>
              ) : (
                <span
                  className="grid size-12 shrink-0 place-items-center rounded-xl border border-white/15 bg-white/10 font-display text-lg font-extrabold"
                  aria-hidden="true"
                >
                  {brand.slice(0, 1).toUpperCase() || "E"}
                </span>
              )}
              <span className="truncate font-display text-xl font-bold tracking-tight">
                {brand}
              </span>
            </Link>
          </div>

          <FooterSection title={t("footer.explore")} ariaLabel={t("nav.main")}>
            <FooterLink to="/">{t("nav.home")}</FooterLink>
            <FooterLink to="/branches">{t("nav.branches")}</FooterLink>
            <FooterLink to="/main-menu">{t("nav.menu")}</FooterLink>
          </FooterSection>

          {supportPhone ? (
            <FooterSection title={t("footer.contact")}>
              <a
                className={footerLinkClass}
                href={`tel:${supportPhone}`}
                aria-label={`${t("footer.callUs")}: ${supportPhone}`}
              >
                <Phone
                  className="size-4 shrink-0 text-gold"
                  aria-hidden="true"
                />
                <span
                  dir="ltr"
                  className="whitespace-nowrap text-start text-xs sm:text-sm"
                >
                  {supportPhone}
                </span>
              </a>
            </FooterSection>
          ) : null}

          {legalLinks.length ? (
            <FooterSection
              title={t("footer.legal")}
              ariaLabel={t("nav.legal")}
              horizontalOnMobile
            >
              {legalLinks.map((link) => (
                <FooterLink key={link.to} to={link.to}>
                  {link.label}
                </FooterLink>
              ))}
            </FooterSection>
          ) : null}
        </div>

        {availableSocialLinks.length ? (
          <nav
            aria-label={t("footer.social")}
            className="flex flex-wrap items-center justify-center gap-1 pb-3"
          >
            {availableSocialLinks.map(({ key, label, href, Icon }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
                className="grid size-11 place-items-center text-primary-foreground/75 transition-colors hover:text-gold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
              >
                <Icon aria-hidden="true" className="size-5" />
              </a>
            ))}
          </nav>
        ) : null}

        <div className="flex flex-col gap-2 border-t border-white/15 py-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs font-medium tracking-wide text-primary-foreground/55">
            {t("footer.rights", { year, brand })}
          </span>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-primary-foreground/65">
            <span>{t("footer.poweredPrefix")}</span>
            <a
              href="https://www.emynor.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("footer.emynorLink")}
              className="group inline-flex min-h-11 items-center gap-2 rounded-lg text-primary-foreground transition-colors hover:text-gold"
            >
              <img
                src="/EmynorWordmarkTransparent.png"
                alt=""
                width="98"
                height="36"
                loading="lazy"
                className="h-8 w-auto shrink-0 transition-transform group-hover:-translate-y-0.5"
              />
              <ArrowUpRight
                className="size-3.5 opacity-60 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 rtl:rotate-[-90deg] rtl:group-hover:-translate-x-0.5"
                aria-hidden="true"
              />
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterSection({
  title,
  ariaLabel,
  horizontalOnMobile = false,
  children,
}: {
  title: string;
  ariaLabel?: string;
  horizontalOnMobile?: boolean;
  children: ReactNode;
}) {
  const linksClass = horizontalOnMobile
    ? "mt-1 flex flex-wrap items-center gap-x-5 lg:flex-col lg:items-start lg:gap-x-0"
    : "mt-1 flex flex-col items-start";

  return (
    <section
      className={
        horizontalOnMobile ? "col-span-2 lg:col-span-2" : "lg:col-span-2"
      }
    >
      <h2 className="text-base font-semibold uppercase tracking-[0.08em] text-gold">
        {title}
      </h2>
      {ariaLabel ? (
        <nav aria-label={ariaLabel} className={linksClass}>
          {children}
        </nav>
      ) : (
        <div className={linksClass}>{children}</div>
      )}
    </section>
  );
}

function FooterLink({
  to,
  children,
}: {
  to:
    | "/"
    | "/branches"
    | "/main-menu"
    | "/privacy-policy"
    | "/terms-and-conditions"
    | "/return-policy";
  children: ReactNode;
}) {
  return (
    <Link to={to} className={footerLinkClass}>
      <span
        className="h-px w-0 bg-gold transition-[width] duration-200 group-hover:w-3"
        aria-hidden="true"
      />
      <span>{children}</span>
    </Link>
  );
}
